'use client'

import { useState } from "react"
import { ChevronDown } from "lucide-react"
import { ProductAnalysis } from "@/lib/product-analytics"
import { normalizeDimensions, formatDimensions } from "@/lib/dimension-utils"
import { formatSpecValue, truncateSpecNumber } from "@/lib/format-utils"
import {
  dedupeSpecsByLabel,
  isOpaqueCodeValue,
  isSpecKeyHidden,
  titleCaseValue,
} from "@/lib/spec-display-policy"

interface TechnicalSpecsProps {
  product: any
  productData: any
  analysis: ProductAnalysis
  /** Conversion spec P2 (SPECS-flag-gated upstream): "best data wins" rows
   * from getDisplaySpecs (manufacturer > retailer sources > specs_json),
   * each carrying provenance. Appended AFTER the legacy rows and deduped by
   * label, so existing rows always win and prod is unchanged until the
   * owner flips NEXT_PUBLIC_MODULE_SPECS. */
  extraSpecs?: Array<{ label: string; value: string; source: string }> | null
  /** FRIDGE-149 §2 — the PDP renders this block twice (a desktop instance in
   * the gallery column that sits BEFORE the H1 in DOM order, and a mobile
   * instance after it), so the desktop copy's title must not be a heading or
   * the outline opens with "Technical Specs" instead of the product name.
   * "p" renders the same title text with the same classes as a paragraph and
   * names the section via aria-label instead. Default "h2". */
  titleAs?: "h2" | "p"
}

export function TechnicalSpecs({ product, productData, analysis, extraSpecs, titleAs = "h2" }: TechnicalSpecsProps) {
  // Long spec tables collapse to keep the gallery column's baseline height
  // compact — that compact baseline is what the sticky-column chooser measures
  // at mount to decide which hero column pins (expanding later doesn't re-flow
  // the sticky column, by design — see hooks/use-sticky-column.ts).
  const [showAllSpecs, setShowAllSpecs] = useState(false)
  // Track which specs_json keys we've already displayed to avoid duplicates.
  // Anything in here is rendered explicitly above (with formatting/units) so
  // the dynamic loop must skip it.
  const displayedKeys = new Set<string>([
    'energy_star', 'ice_maker', 'water_dispenser', 'wifi_enabled',
    'shelf_material', 'defrost_type', 'interior_lighting', 'temperature_zones',
    'warranty', 'noise_level', 'number_of_doors', 'installation',
    'freezer_capacity', 'fresh_food_capacity', 'rating', 'reviews',
    // `color` is rendered explicitly from `productData.product.color`. The
    // dynamic loop must skip the duplicate `specs_json.color` row, otherwise
    // we render "Color: White" + "Color: WHITE" (Demuller PDP, May 9, 2026).
    'color',
    // Energy Star detail rows are rendered explicitly above via
    // `productData.product.energyStarData` (see "vs. Federal Standard" block).
    // Skip the raw specs_json equivalents so we don't render them twice.
    'energy_star_most_efficient',
    'pct_better_than_federal',
    // `auto_defrost` would conflict with the explicit "Defrost Type" row
    // above (defrost_type). Centralize on the explicit row to avoid two
    // defrost-related entries fighting for the same fact.
    'auto_defrost',
    // `reversible_door` is rendered explicitly via the door-config rows.
    // Centralize here to avoid the dynamic loop adding a second row.
    'reversible_door',
  ])

  // Helper to format key names for display
  const formatKey = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Helper to format values. Numbers and numeric text run through the shared
  // spec formatter so long decimals truncate to ≤2 places (owner spec 07/20:
  // 36.99999996226 → 36.99). Render-time only — stored data is untouched.
  const formatValue = (value: any): string => {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') return truncateSpecNumber(value)
    if (typeof value === 'string') return formatSpecValue(value)
    if (Array.isArray(value)) return value.join(', ')
    return String(value)
  }

  // Collect all specs to display
  const specs: Array<{ label: string; value: string }> = []

  // Helper functions for inference
  const title = (product.name || '').toLowerCase()
  const getInferred = (regex: RegExp) => title.match(regex)?.[1]
  const hasKeyword = (key: string) => title.includes(key)

  // Add known fields first (with nice formatting and units)
  
  // Dimensions — must go through normalizer. Raw width_in/height_in/depth_in
  // can contain mm-stored-as-inches (e.g. Samsung RF23DB9750QLAA: 912 × 1853 ×
  // 731), so reading them as `${x}"` produces "912\" × 1853\" × 731\"" — the
  // 2026-05-07 incident bug. The normalizer detects unit, converts on
  // consensus, and returns "" when data is corrupted so we hide the row
  // instead of misleading users. See lib/dimension-utils.ts.
  const rawDims = {
    width_in: productData.product.width_in ?? productData.product.dimensions?.width,
    height_in: productData.product.height_in ?? productData.product.dimensions?.height,
    depth_in: productData.product.depth_in ?? productData.product.dimensions?.depth,
  }
  // FRIDGE-688: the label must promise exactly what the value delivers. A
  // width-only record used to render '30"' under 'Dimensions (W×H×D)' — a
  // one-number value beneath a three-dimension promise. Count the parts the
  // normalizer actually produced and label truthfully: the full triple keeps
  // the W×H×D label; one axis gets its own name; two axes get the labeled
  // style whose values name themselves ('30" W × 28" D').
  const dimsNormalized = normalizeDimensions(rawDims, productData.product.type)
  const dimParts = [
    { key: 'W', label: 'Width', value: dimsNormalized?.widthInches ?? null },
    { key: 'H', label: 'Height', value: dimsNormalized?.heightInches ?? null },
    { key: 'D', label: 'Depth', value: dimsNormalized?.depthInches ?? null },
  ].filter((d) => d.value !== null && d.value !== undefined)
  if (dimParts.length === 3) {
    const dimsCompact = formatDimensions(dimsNormalized, { style: 'compact' })
    if (dimsCompact) specs.push({ label: 'Dimensions (W×H×D)', value: dimsCompact })
  } else if (dimParts.length === 2) {
    const dimsLabeledPair = formatDimensions(dimsNormalized, { style: 'labeled' })
    if (dimsLabeledPair) specs.push({ label: 'Dimensions', value: dimsLabeledPair })
  } else if (dimParts.length === 1) {
    const only = formatDimensions(dimsNormalized, { style: 'compact' })
    if (only) specs.push({ label: dimParts[0].label, value: only })
  }

  // Capacity: Volume
  if (product.capacity_cuft || getInferred(/(\d+\.?\d*)\s*cu\.?\s*ft/i)) {
    specs.push({
      label: 'Total Capacity',
      value: `${formatSpecValue(product.capacity_cuft || getInferred(/(\d+\.?\d*)\s*cu\.?\s*ft/i))} cubic feet`
    })
  }

  // Capacity: Bottles
  if (analysis.features.specialFeatures.find(f => f.includes('bottles')) || getInferred(/(\d+)\s*-?\s*bottle/i)) {
    specs.push({
      label: 'Bottle Capacity',
      value: analysis.features.specialFeatures.find(f => f.includes('bottles'))?.replace('Holds up to ', '') || `${getInferred(/(\d+)\s*-?\s*bottle/i)} Bottles`
    })
  }

  // Capacity: Cans
  if (analysis.features.specialFeatures.find(f => f.includes('cans')) || getInferred(/(\d+)\s*-?\s*can/i)) {
    specs.push({
      label: 'Can Capacity',
      value: analysis.features.specialFeatures.find(f => f.includes('cans'))?.replace('Holds up to ', '') || `${getInferred(/(\d+)\s*-?\s*can/i)} Cans`
    })
  }

  // Kegerator Specs
  if (analysis.features.specialFeatures.find(f => f.includes('Tap')) || getInferred(/(\d+)\s*-?\s*tap/i) || hasKeyword('single tap') || hasKeyword('dual tap')) {
    specs.push({
      label: 'Draft System',
      value: analysis.features.specialFeatures.find(f => f.includes('Tap')) || (getInferred(/(\d+)\s*-?\s*tap/i) ? `${getInferred(/(\d+)\s*-?\s*tap/i)} Tap` : hasKeyword('dual tap') ? 'Dual Tap' : 'Single Tap')
    })
  }

  // Zones
  if (analysis.features.specialFeatures.find(f => f.includes('Dual Zone')) || hasKeyword('dual zone')) {
    specs.push({ label: 'Temperature Zones', value: 'Dual Zone' })
  }

  // Security
  if (analysis.features.specialFeatures.find(f => f.includes('Lock')) || hasKeyword('lock')) {
    specs.push({ label: 'Security', value: 'Integrated Lock' })
  }

  // Finish (Inferred)
  if (!productData.product.color && (hasKeyword('stainless') || hasKeyword('black') || hasKeyword('white'))) {
    specs.push({
      label: 'Finish',
      value: hasKeyword('stainless') ? 'Stainless Steel' : hasKeyword('black') ? 'Black' : 'White'
    })
  }

  // Installation (Inferred)
  if (!product.specs_json?.installation && (hasKeyword('built-in') || hasKeyword('undercounter') || hasKeyword('freestanding'))) {
    specs.push({
      label: 'Installation',
      value: hasKeyword('built-in') ? 'Built-In' : hasKeyword('undercounter') ? 'Undercounter' : 'Freestanding'
    })
  }

  // Energy Usage
  if (productData.product.energy_kwh_yr) {
    specs.push({ label: 'Energy Usage', value: `${formatSpecValue(productData.product.energy_kwh_yr)} kWh/year` })
  }

  // Energy Star
  if (!['conflict', 'ambiguous'].includes(productData.product.energyEvidence?.status) && typeof product.specs_json?.energy_star === 'boolean') {
    specs.push({ label: 'Energy Star Certified', value: product.specs_json.energy_star ? 'Yes ⚡' : 'No' })
  }

  // Federal Standard Comparison (from EPA Energy Star Certified Products Database)
  if (productData.product.energyStarData) {
    const esData = productData.product.energyStarData
    if (esData.pctBetterThanFederal != null && esData.federalStandardKwh) {
      specs.push({
        label: 'Calculated vs. Federal Standard',
        value: `${formatSpecValue(Math.abs(esData.pctBetterThanFederal))}% ${esData.pctBetterThanFederal >= 0 ? 'below' : 'above'} (uses ${formatSpecValue(esData.certifiedKwh)} kWh/year vs ${formatSpecValue(esData.federalStandardKwh)} kWh/year federal standard)`
      })
    }
  }

  // Weight
  if (productData.product.weight_lbs) {
    specs.push({ label: 'Weight', value: `${formatSpecValue(productData.product.weight_lbs)} lbs` })
  }

  // Door Configuration
  if (product.specs_json?.number_of_doors) {
    specs.push({ label: 'Door Configuration', value: `${product.specs_json.number_of_doors} Doors` })
  }

  // Installation Type
  if (product.specs_json?.installation) {
    specs.push({ label: 'Installation Type', value: product.specs_json.installation })
  }

  // Color/Finish — normalize ALL-CAPS Keepa values ("WHITE") into Title Case
  // ("White"). The case-insensitive dedupe step below collapses the partner
  // `specs_json.color` row, but we still want this row to read as a proper
  // noun on its own.
  if (productData.product.color) {
    const raw = String(productData.product.color)
    specs.push({ label: 'Color', value: /[a-z]/.test(raw) ? raw : titleCaseValue(raw) })
  }

  // Freezer Capacity
  if (product.specs_json?.freezer_capacity) {
    specs.push({ label: 'Freezer Capacity', value: `${formatSpecValue(product.specs_json.freezer_capacity)} cu ft` })
  }

  // Fresh Food Capacity
  if (product.specs_json?.fresh_food_capacity) {
    specs.push({ label: 'Fresh Food Capacity', value: `${formatSpecValue(product.specs_json.fresh_food_capacity)} cu ft` })
  }

  // Ice Maker
  if (typeof product.specs_json?.ice_maker === 'boolean') {
    specs.push({ label: 'Ice Maker', value: product.specs_json.ice_maker ? 'Built-in' : 'Not included' })
  }

  // Water Dispenser
  if (typeof product.specs_json?.water_dispenser === 'boolean') {
    specs.push({ label: 'Water Dispenser', value: product.specs_json.water_dispenser ? 'Yes' : 'No' })
  }

  // WiFi/Smart Features
  if (typeof product.specs_json?.wifi_enabled === 'boolean') {
    specs.push({ label: 'WiFi/Smart Features', value: product.specs_json.wifi_enabled ? 'Yes' : 'No' })
  }

  // Shelf Material
  if (product.specs_json?.shelf_material) {
    specs.push({ label: 'Shelf Material', value: product.specs_json.shelf_material })
  }

  // Defrost Type
  if (product.specs_json?.defrost_type) {
    specs.push({ label: 'Defrost Type', value: product.specs_json.defrost_type })
  }

  // Interior Lighting
  if (product.specs_json?.interior_lighting) {
    specs.push({ label: 'Interior Lighting', value: product.specs_json.interior_lighting })
  }

  // Temperature Zones
  if (product.specs_json?.temperature_zones) {
    specs.push({ label: 'Temperature Zones', value: product.specs_json.temperature_zones })
  }

  // Warranty
  if (product.specs_json?.warranty) {
    specs.push({ label: 'Warranty', value: product.specs_json.warranty })
  }

  // Noise Level
  if (product.specs_json?.noise_level) {
    specs.push({ label: 'Noise Level', value: `${formatSpecValue(product.specs_json.noise_level)} dB` })
  }

  // DYNAMIC SECTION: Render ALL remaining specs_json fields
  if (product.specs_json) {
    Object.entries(product.specs_json).forEach(([key, value]) => {
      // Skip if already displayed above
      if (displayedKeys.has(key)) return

      // Brand Policy: hide Amazon/Keepa-internal keys (amazon_*, keepa_*,
      // csv_*, asin*, list_price_usd, monthly_sold, item_type, etc.).
      // See lib/spec-display-policy.ts. Filter at display time only — the
      // underlying specs_json row is left intact for other consumers
      // (recommendations, FAQ generation, fact-checking).
      if (isSpecKeyHidden(key)) return

      // Skip null/undefined/empty values
      if (value === null || value === undefined || value === '' || value === 0) return

      // Skip internal/meta fields
      if (key.startsWith('_') || key === 'id' || key === 'created_at' || key === 'updated_at') return

      // Skip objects (too complex to display inline)
      if (typeof value === 'object' && !Array.isArray(value)) return

      // Normalize ALL-CAPS string values from Keepa exports to Title Case
      // ("WHITE" → "White") so the dedupe step below can collapse them
      // against the explicitly-rendered `productData.product.color` row.
      // Opaque codes (single token with digits — model numbers, SKUs) are
      // exempt: title-casing "CWF440SZ" corrupts it (FRIDGE-280 08/29/2026).
      const normalizedValue =
        typeof value === 'string' &&
        /^[A-Z0-9\s\-_/]+$/.test(value) &&
        /[A-Z]/.test(value) &&
        !isOpaqueCodeValue(value)
          ? titleCaseValue(value)
          : formatValue(value)

      specs.push({
        label: formatKey(key),
        value: normalizedValue,
      })
    })
  }

  // DYNAMIC SECTION: Render additional productData.product fields
  const additionalFields = ['finish', 'voltage', 'amperage', 'refrigerant_type', 'compressor_type', 'door_swing']
  additionalFields.forEach(key => {
    const value = (productData.product as any)[key]
    if (value && !displayedKeys.has(key) && !isSpecKeyHidden(key)) {
      specs.push({
        label: formatKey(key),
        value: formatValue(value)
      })
    }
  })

  // P2: append the resolver-sourced rows last so every legacy row wins the
  // label-dedupe below — extraSpecs only ADD coverage, never change an
  // existing row's value.
  if (extraSpecs && extraSpecs.length > 0) {
    for (const e of extraSpecs) {
      if (e?.label && e?.value) specs.push({ label: e.label, value: e.value })
    }
  }

  // Final pass: collapse any rows whose labels collide case-insensitively
  // (e.g. two "Color" entries from products.color + specs_json.color). The
  // dedupe also normalizes leftover ALL-CAPS values to Title Case.
  const dedupedSpecs = dedupeSpecsByLabel(specs)

  // If no specs, return null
  const energySourceNote = productData.product.energyEvidence?.note
  if (dedupedSpecs.length === 0 && !energySourceNote) return null

  // Collapse long tables: show the first 12 rows + a toggle when there are
  // enough hidden rows to be worth it (avoids a silly "Show all 13").
  const COLLAPSE_THRESHOLD = 15
  const COLLAPSED_COUNT = 12
  const collapsible = dedupedSpecs.length >= COLLAPSE_THRESHOLD
  const visibleSpecs = collapsible && !showAllSpecs ? dedupedSpecs.slice(0, COLLAPSED_COUNT) : dedupedSpecs

  return (
    <section
      className="bg-[#0469f0]/[0.04] rounded-lg p-5 md:p-6"
      aria-label={titleAs === "p" ? "Technical Specs" : undefined}
    >
      <header className="mb-5">
        <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">
          Specifications
        </p>
        {titleAs === "p" ? (
          <p className="text-xl md:text-2xl font-black tracking-tighter text-gray-900">
            Technical Specs
          </p>
        ) : (
          <h2 className="text-xl md:text-2xl font-black tracking-tighter text-gray-900">
            Technical Specs
          </h2>
        )}
      </header>
      {energySourceNote && (
        <p className="mb-4 rounded-md bg-white p-3 text-sm leading-relaxed text-gray-800" role="note">
          <strong>Energy source check: </strong>{energySourceNote}
        </p>
      )}
      {/* CSS columns (not a grid): each row is independent, so a value that
          wraps only grows its own row instead of forcing a row-paired grid to
          stretch its partner cell and stagger the hairlines (the misalignment
          fixed 2026-07-10). break-inside-avoid keeps a label/value pair whole. */}
      <div className="columns-1 md:columns-2 md:gap-x-10">
        {visibleSpecs.map((spec, index) => (
          <div key={index} className="flex items-baseline justify-between gap-4 py-2.5 border-b border-gray-200/70 break-inside-avoid">
            <span className="text-gray-500 text-sm">{spec.label}</span>
            <span className="text-gray-900 font-semibold text-sm text-right tabular-nums">{spec.value}</span>
          </div>
        ))}
      </div>
      {collapsible && (
        <button
          type="button"
          onClick={() => setShowAllSpecs((v) => !v)}
          aria-expanded={showAllSpecs}
          className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#0469f0] hover:text-[#004aad] transition-colors focus-visible:outline-none focus-visible:underline"
          data-testid="specs-collapse-toggle"
        >
          {showAllSpecs ? 'Show fewer specs' : `Show all ${dedupedSpecs.length} specs`}
          <ChevronDown className={`w-4 h-4 transition-transform ${showAllSpecs ? 'rotate-180' : ''}`} />
        </button>
      )}
    </section>
  )
}
